import type { PublicPageLink } from '@/features/common/types/PublicPageLink';
import type { ContentPage } from '@/services/types/ContentPage';
import { apiDelete, apiGet, apiPost, apiPut } from '@/services/api';

const ADMIN = '/api/v1/admin/content';

/**
 * content_item's admin endpoints, shared by the two screens over it — Noutăți (NEWS/ARTICLE) and
 * Pagini și politici (PAGE/POLICY). ContentAdminController's `type` filter is generic, so the
 * screens differ only in the types they ask for and the fields they send.
 */
export const contentApi = {
  list: <T>(types: string[]) =>
    apiGet<T[]>(`${ADMIN}?${types.map((type) => `type=${type}`).join('&')}`),
  /**
   * One page of the admin table, searched, filtered and ordered by the server, with what the screen
   * used to work out from holding every row: the N of "x din N" and the categories its filter
   * offers. `query` carries `page`/`size`, which is what asks for this envelope rather than the
   * array `list` gets.
   */
  page: <T>(query: URLSearchParams) => apiGet<ContentPage<T>>(`${ADMIN}?${query}`),
  publicPage: <T>(slug: string) => apiGet<T>(`/api/v1/public/pages/${slug}`),
  /** Every published PAGE, title only, in the reader's language — for the menu and the sitemap. */
  publicPages: () => apiGet<PublicPageLink[]>('/api/v1/public/pages'),
  /** Creates an item through the contribution endpoint, which also opens its workflow instance. */
  create: (body: unknown) => apiPost('/api/v1/content', body),
  update: (id: string, body: unknown) => apiPut(`${ADMIN}/${id}`, body),
  /** A hard delete — there is no soft-delete column to restore from; only the audit record stays. */
  remove: (id: string) => apiDelete(`${ADMIN}/${id}`),
  /**
   * The legacy-content fallback: a seeded item (or one retracted through here) has no workflow
   * instance, so the generic "submit" action has nothing to act on — see
   * ContentAdminController#publishDirect.
   */
  publishDirect: (id: string) => apiPost(`${ADMIN}/${id}/publish`, {}),
  retractDirect: (id: string) => apiPost(`${ADMIN}/${id}/retract`, {}),
};
